import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { environment } from '@ion/env';
import { ErrorModule } from '@ion/error';
import { FeatureTogglingConfig, provideFeatureToggling } from '@ion/feature-toggling';
import { ValidationErrors } from '@ion/forms';
import { AuthModule } from '@ion/iam';
import {
  currentTargetTimeZoneProvider,
  DateTransformer,
  THEME_SWITCHER,
  TimeTransformer,
  TimeZoneDateTimeTransformer,
  UiService,
} from '@ion/misc';
import { CONFIRMATION_PROVIDER, MODAL_PROVIDER } from '@ion/notifications';
import { provideModuleTranslateLoader } from '@larscom/ngx-translate-module-loader';
import { provideTranslateService } from '@ngx-translate/core';
import { tuiDateFormatProvider, tuiNotificationOptionsProvider } from '@taiga-ui/core';
import { provideEventPlugins } from '@taiga-ui/event-plugins';
import { TuiLanguageName, tuiLanguageSwitcher } from '@taiga-ui/i18n';
import {
  TUI_DATE_TIME_VALUE_TRANSFORMER,
  TUI_DATE_VALUE_TRANSFORMER,
  TUI_INPUT_DATE_TIME_OPTIONS,
  TUI_RADIO_DEFAULT_OPTIONS,
  TUI_RADIO_OPTIONS,
  TUI_TIME_VALUE_TRANSFORMER,
  TUI_VALIDATION_ERRORS,
  tuiInputDateOptionsProviderNew,
  tuiInputDateTimeOptionsProvider,
  tuiInputTimeOptionsProvider,
} from '@taiga-ui/kit';
import { DateTime } from 'luxon';
import { provideHighlightOptions } from 'ngx-highlightjs';

import { apiConfigs } from './api.config';
import { AppRoutes } from './app.routes';
import { modules } from './translate-loader-modules';

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(environment.enableAuthentication ? AuthModule : [], ErrorModule, BrowserModule, ...apiConfigs),
    provideTranslateService({
      loader: provideModuleTranslateLoader({
        modules: modules,
        disableNamespace: true,
      }),
    }),
    tuiNotificationOptionsProvider({
      // FIXME: duration is not supported in Taiga UI v4
      // duration: 5000,
      icon: '@tui.material.outlined.report_problem',
    }),
    tuiDateFormatProvider({ mode: 'DMY', separator: '.' }),
    MODAL_PROVIDER,
    CONFIRMATION_PROVIDER,
    provideHighlightOptions({
      coreLibraryLoader: () => import('highlight.js/lib/core'),
      languages: {
        json: () => import('highlight.js/lib/languages/json'),
      },
    }),
    {
      provide: TUI_RADIO_OPTIONS,
      useValue: {
        ...TUI_RADIO_DEFAULT_OPTIONS,
        appearances: {
          unchecked: 'radio-unchecked',
          checked: 'radio-checked',
        },
      },
    },

    // Taiga UI default transformers configs:
    // See docs at libs/shared-ui/misc/src/lib/temporal/README.md
    currentTargetTimeZoneProvider(DateTime.local().zoneName),

    // WARN These three transformer providers are for @taiga-ui/legacy components
    { provide: TUI_DATE_VALUE_TRANSFORMER, useClass: DateTransformer }, // TODO This is a legacy transformer, please check how we can substitute it with the new one (DayTransformer)
    { provide: TUI_TIME_VALUE_TRANSFORMER, useClass: TimeTransformer },
    { provide: TUI_DATE_TIME_VALUE_TRANSFORMER, useClass: TimeZoneDateTimeTransformer },

    // These next three option providers (containing transformers) are for Taiga UI v4 components
    tuiInputDateOptionsProviderNew({ valueTransformer: new DateTransformer() }),
    tuiInputTimeOptionsProvider({ valueTransformer: new TimeTransformer() }),
    /* Frustratingly, tuiInputDateTimeOptionsProvider can't be used normally because it *requires* an object,
     * but TimeZoneDateTimeTransformer needs to call inject() during construction:
     *   tuiInputDateTimeOptionsProvider({ valueTransformer: new TimeZoneDateTimeTransformer() }),
     * It's also not possible to inject the default options here, because we are in the root injector context.
     * Luckily the default options are ALSO defined in the tuiInputDateTimeOptionsProvider function, so we can
     * manually create the provider while calling the default factory function: */
    {
      provide: TUI_INPUT_DATE_TIME_OPTIONS,
      useFactory: () =>
        // taiga-ui is setting any as provider here and i do not want to mess around with that too much tbh
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        tuiInputDateTimeOptionsProvider({
          valueTransformer: new TimeZoneDateTimeTransformer(),
        }).useFactory(), // What a mess 😂
    },

    {
      provide: THEME_SWITCHER,
      useExisting: UiService,
    },
    { provide: TUI_VALIDATION_ERRORS, useClass: ValidationErrors },
    // See: https://taiga-ui.dev/i18n/Dynamic_loader
    // See: https://github.com/Tinkoff/taiga-ui/issues/2991
    tuiLanguageSwitcher(async (language: TuiLanguageName): Promise<unknown> => {
      if (language === 'german') {
        return import('@taiga-ui/i18n/languages/german');
      }
      if (language === 'english') {
        return import('@taiga-ui/i18n/languages/english');
      }
      return new Error('No language specified for tuiLanguageSwitcher');
    }),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(AppRoutes, withComponentInputBinding()),
    provideFeatureToggling(environment as FeatureTogglingConfig),
    provideEventPlugins(),
  ],
};
